<template>
  <Dialog :open="isOpen" @update:open="(value) => !value && handleClose()">
    <DialogContent class="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle class="flex items-center gap-2">
          <Flag class="w-5 h-5 text-yellow-500" />
          Issue Warning
        </DialogTitle>
        <DialogDescription>
          Please provide details about the warning you'd like to issue. This action will be recorded and cannot be undone.
        </DialogDescription>
      </DialogHeader>

      <form @submit.prevent="handleSubmit" class="space-y-4">
        <!-- Fellow Selection -->
        <div class="space-y-2">
          <label class="text-sm font-medium text-gray-700">
            Fellow to Issue a Warning to <span class="text-red-500">*</span>
          </label>
          <Popover v-model:open="open">
            <PopoverTrigger as-child>
              <Button
                variant="outline"
                role="combobox"
                :aria-expanded="open"
                class="w-full justify-between"
                :disabled="isLoading"
              >
                {{ selectedUser ? `${selectedUser.first_name} ${selectedUser.last_name} (@${selectedUser.username})` : 'Select a fellow...' }}
                <ChevronsUpDown class="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent class="w-full p-0">
              <Command v-model="form.fellow_username">
                <CommandInput placeholder="Search fellows..." />
                <CommandEmpty>No fellows found.</CommandEmpty>
                <CommandList>
                  <CommandGroup>
                    <CommandItem
                      v-for="user in filteredUsers"
                      :key="user.username"
                      :value="user.username"
                      @select="handleUserSelection(user)"
                    >
                      <Check
                        :class="cn(
                          'h-4 w-4',
                          form.fellow_username === user.username ? 'opacity-100' : 'opacity-0',
                        )"
                      />
                      {{ user.first_name }} {{ user.last_name }} (@{{ user.username }})
                    </CommandItem>
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          <div v-if="errors.fellow_username" class="text-sm text-red-600">
            {{ errors.fellow_username }}
          </div>
        </div>

        <!-- Reason Selection -->
        <div class="space-y-2">
          <label class="text-sm font-medium text-gray-700">
            Reason <span class="text-red-500">*</span>
          </label>
          <Select v-model="form.reason" :disabled="isLoading">
            <SelectTrigger class="w-full">
              <SelectValue placeholder="Select a reason..." />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value="UNPROFESSIONAL">Unprofessional conduct</SelectItem>
                <SelectItem value="COMMUNICATION">Poor communication</SelectItem>
                <SelectItem value="ATTENDANCE">Attendance issues</SelectItem>
                <SelectItem value="PERFORMANCE">Performance concerns</SelectItem>
                <SelectItem value="CODE_OF_CONDUCT">Code of conduct violation</SelectItem>
                <SelectItem value="HARASSMENT">Harassment or inappropriate behavior</SelectItem>
                <SelectItem value="OTHER">Other</SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
          <div v-if="errors.reason" class="text-sm text-red-600">
            {{ errors.reason }}
          </div>
        </div>

        <!-- Severity Selection -->
        <div class="space-y-2">
          <label class="text-sm font-medium text-gray-700">
            Severity <span class="text-red-500">*</span>
          </label>
          <Select v-model="form.severity" :disabled="isLoading">
            <SelectTrigger class="w-full">
              <SelectValue placeholder="Select severity level..." />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value="MINOR">Minor</SelectItem>
                <SelectItem value="MODERATE">Moderate</SelectItem>
                <SelectItem value="SEVERE">Severe</SelectItem>
                <SelectItem value="CRITICAL">Critical</SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
          <div v-if="errors.severity" class="text-sm text-red-600">
            {{ errors.severity }}
          </div>
        </div>

        <!-- Description -->
        <div class="space-y-2">
          <label class="text-sm font-medium text-gray-700">
            Description <span class="text-red-500">*</span>
          </label>
          <Textarea
            v-model="form.description"
            placeholder="Provide a detailed description of the incident..."
            rows="4"
            :maxlength="2000"
            :disabled="isLoading"
            class="resize-none"
          />
          <div class="flex justify-between text-xs text-gray-500">
            <div v-if="errors.description" class="text-red-600">
              {{ errors.description }}
            </div>
            <div class="ml-auto">
              {{ form.description.length }}/2000
            </div>
          </div>
        </div>

        <!-- Action Buttons -->
        <DialogFooter class="flex gap-2 pt-4">
          <Button
            type="button"
            variant="outline"
            @click="handleClose"
            :disabled="isLoading"
            class="text-xs"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            :disabled="isLoading || !isFormValid"
            class="bg-red-600 hover:bg-red-700 text-white text-xs w-fit"
          >
            <Loader2 v-if="isLoading" class="w-4 h-4 mr-2 animate-spin" />
            Issue Warning
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { Loader2, ChevronsUpDown, Check } from 'lucide-vue-next'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import api from '@/utils/api'
import { API_ROUTES } from '@/const/apiRoutes'
import { useToast } from 'primevue/usetoast'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { cn } from '@/lib/utils'

interface User {
  id: number
  username: string
  first_name: string
  last_name: string
  email: string
}

interface StrikeForm {
  fellow_username: string
  reason: string
  severity: string
  description: string
}

const props = defineProps<{
  isOpen: boolean
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'submitted'): void
}>()

const toast = useToast()

// State
const isLoading = ref(false)
const userList = ref<User[]>([])
const selectedUser = ref<User | null>(null)
const open = ref(false)
const form = ref<StrikeForm>({
  fellow_username: '',
  reason: '',
  severity: '',
  description: ''
})

const errors = ref<Partial<Record<keyof StrikeForm, string>>>({})

// Computed
const filteredUsers = computed(() => {
  return userList.value
})

const isFormValid = computed(() => {
  return (
    form.value.fellow_username.trim() !== '' &&
    form.value.reason.trim() !== '' &&
    form.value.severity.trim() !== '' &&
    form.value.description.trim() !== '' &&
    form.value.description.length <= 2000 &&
    Object.keys(errors.value).length === 0
  )
})

// Methods
const fetchUserList = async () => {
  try {
    const response = await api.get(API_ROUTES.GET_USER_LIST)
    // Sort users alphabetically by first name, then last name
    userList.value = response.data.sort((a: User, b: User) => {
      const nameA = `${a.first_name} ${a.last_name}`.toLowerCase()
      const nameB = `${b.first_name} ${b.last_name}`.toLowerCase()
      return nameA.localeCompare(nameB)
    })
  } catch (error) {
    console.error('Error fetching user list:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to load user list',
      life: 3000
    })
  }
}

const handleUserSelection = (user: User) => {
  selectedUser.value = user
  form.value.fellow_username = user.username
  open.value = false
}

const validateForm = () => {
  errors.value = {}

  if (!form.value.fellow_username.trim()) {
    errors.value.fellow_username = 'Please select a fellow to issue a warning to'
  }

  if (!form.value.reason.trim()) {
    errors.value.reason = 'Please select a reason'
  }

  if (!form.value.severity.trim()) {
    errors.value.severity = 'Please select severity level'
  }

  if (!form.value.description.trim()) {
    errors.value.description = 'Description is required'
  } else if (form.value.description.length > 2000) {
    errors.value.description = 'Description must be 2000 characters or less'
  }
}

const resetForm = () => {
  form.value = {
    fellow_username: '',
    reason: '',
    severity: '',
    description: ''
  }
  errors.value = {}
  selectedUser.value = null
  open.value = false
}

const handleSubmit = async () => {
  validateForm()

  if (!isFormValid.value) {
    return
  }

  isLoading.value = true

  try {
    await api.post(API_ROUTES.ISSUE_FELLOW_STRIKE, form.value)

    toast.add({
      severity: 'success',
      summary: 'Warning Issued',
      detail: 'The warning has been issued successfully.',
      life: 5000
    })

    emit('submitted')
    emit('close')
    resetForm()
  } catch (error: any) {
    console.error('Error issuing warning:', error)
    
    let errorMessage = 'Failed to issue warning. Please try again.'
    
    if (error.response?.data?.detail) {
      errorMessage = error.response.data.detail
    } else if (error.response?.data?.message) {
      errorMessage = error.response.data.message
    }

    toast.add({
      severity: 'error',
      summary: 'Warning Failed',
      detail: errorMessage,
      life: 5000
    })
  } finally {
    isLoading.value = false
  }
}

const handleClose = () => {
  if (!isLoading.value) {
    resetForm()
    emit('close')
  }
}

// Watchers
watch(() => props.isOpen, (newValue) => {
  if (newValue) {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    fetchUserList()
  }
})

// Lifecycle
onMounted(() => {
  if (props.isOpen) {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    fetchUserList()
  }
})
</script> 